import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { api, type Favorite, type ItemType } from '@/lib/api';
import { log } from '@/lib/log';

// Favorites store. Server is authoritative (per-user, in SQLite); the store
// caches a Set keyed by `${sourceId}:${itemType}:${itemId}` for O(1) lookups
// in tile components. `add`/`remove` are optimistic with rollback on failure.

function fkey(sourceId: number, itemType: ItemType, itemId: string): string {
    return `${sourceId}:${itemType}:${itemId}`;
}

export const useFavoritesStore = defineStore('favorites', () => {
    const favorites = ref<Favorite[]>([]);
    const lookup = ref<Set<string>>(new Set());
    const loaded = ref(false);
    const loading = ref(false);

    function _rebuildLookup() {
        const next = new Set<string>();
        for (const f of favorites.value) next.add(fkey(f.sourceId, f.itemType, f.itemId));
        lookup.value = next;
    }

    async function refresh() {
        loading.value = true;
        try {
            favorites.value = await api.favorites.getAll();
            _rebuildLookup();
            loaded.value = true;
            log.debug('favorites.refresh', { count: favorites.value.length });
        } finally {
            loading.value = false;
        }
    }

    function has(sourceId: number, itemId: string, itemType: ItemType = 'channel'): boolean {
        return lookup.value.has(fkey(sourceId, itemType, itemId));
    }

    async function add(sourceId: number, itemId: string, itemType: ItemType = 'channel') {
        const k = fkey(sourceId, itemType, itemId);
        if (lookup.value.has(k)) return;
        const optimistic: Favorite = { sourceId, itemId, itemType };
        favorites.value = [...favorites.value, optimistic];
        lookup.value = new Set(lookup.value).add(k);
        try {
            await api.favorites.add(sourceId, itemId, itemType);
            log.info('favorites.add', { sourceId, itemId, itemType });
        } catch (err) {
            favorites.value = favorites.value.filter((f) => fkey(f.sourceId, f.itemType, f.itemId) !== k);
            const next = new Set(lookup.value);
            next.delete(k);
            lookup.value = next;
            log.warn('favorites.add_failed', { error: (err as Error).message });
            throw err;
        }
    }

    async function remove(sourceId: number, itemId: string, itemType: ItemType = 'channel') {
        const k = fkey(sourceId, itemType, itemId);
        if (!lookup.value.has(k)) return;
        const before = favorites.value;
        favorites.value = favorites.value.filter((f) => fkey(f.sourceId, f.itemType, f.itemId) !== k);
        const next = new Set(lookup.value);
        next.delete(k);
        lookup.value = next;
        try {
            await api.favorites.remove(sourceId, itemId, itemType);
            log.info('favorites.remove', { sourceId, itemId, itemType });
        } catch (err) {
            favorites.value = before;
            _rebuildLookup();
            log.warn('favorites.remove_failed', { error: (err as Error).message });
            throw err;
        }
    }

    async function toggle(sourceId: number, itemId: string, itemType: ItemType = 'channel') {
        if (has(sourceId, itemId, itemType)) await remove(sourceId, itemId, itemType);
        else await add(sourceId, itemId, itemType);
    }

    const count = computed(() => favorites.value.length);

    return { favorites, lookup, loaded, loading, count, refresh, has, add, remove, toggle };
});
